import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../config/apiConfig';
import ReactMarkdown from 'react-markdown';

// Helper function to get confidence label based on score (1-10)
const getConfidenceLabel = (score: number): string => {
  if (score >= 9) return 'Very High';
  if (score >= 7) return 'High';
  if (score >= 5) return 'Moderate';
  if (score >= 3) return 'Low';
  return 'Very Low';
};

// Helper function to get confidence color based on score (1-10)
const getConfidenceColor = (score: number): string => {
  if (score >= 9) return 'bg-green-500 dark:bg-green-600';
  if (score >= 7) return 'bg-blue-500 dark:bg-blue-600';
  if (score >= 5) return 'bg-yellow-500 dark:bg-yellow-600';
  if (score >= 3) return 'bg-orange-500 dark:bg-orange-600';
  return 'bg-red-500 dark:bg-red-600';
};

// Cache for exchangers and instruments data
const exchangersCache = new Map<string, string[]>();
const instrumentsCache = new Map<string, {symbol: string, description: string}[]>();

// Component for the Financial and Market Tools page
const FinancialToolsPage: React.FC = () => {
  const navigate = useNavigate();
  // Define tab types to avoid comparison errors
  type TabType = 'market-brief' | 'trade-compass';
  const [activeTab, setActiveTab] = useState<TabType>('market-brief');
  
  // State for Market Brief tab
  const [marketBriefQuery, setMarketBriefQuery] = useState<string>('');
  const [marketBriefResult, setMarketBriefResult] = useState<any>(null);
  const [isGeneratingMarketBrief, setIsGeneratingMarketBrief] = useState<boolean>(false);
  const [marketBriefError, setMarketBriefError] = useState<string>('');

  // State for Trade Compass tab
  const [exchangers, setExchangers] = useState<string[]>([]);
  const [selectedExchange, setSelectedExchange] = useState<string>('');
  const [instruments, setInstruments] = useState<{symbol: string, description: string}[]>([]);
  const [selectedInstrument, setSelectedInstrument] = useState<string>('');
  const [timeframe, setTimeframe] = useState<string>('4h');
  const [isLoadingExchangers, setIsLoadingExchangers] = useState<boolean>(false);
  const [isLoadingInstruments, setIsLoadingInstruments] = useState<boolean>(false);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState<boolean>(false);
  const [chartData, setChartData] = useState<{image: string | null, analysis: any} | null>(null);
  const [error, setError] = useState<string>('');
  const [showChartModal, setShowChartModal] = useState(false);

  // Fetch exchangers when Market Brief tab is active
  const fetchExchangers = useCallback(async () => {
    // Check cache first
    if (exchangersCache.has('exchangers')) {
      const cachedExchangers = exchangersCache.get('exchangers') || [];
      setExchangers(cachedExchangers);
      
      // Set default exchange if none is selected yet
      if (!selectedExchange && cachedExchangers.length > 0) {
        setSelectedExchange(cachedExchangers[0]);
      }
      return;
    }
    
    setIsLoadingExchangers(true);
    setError('');
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/chart/exchangers`);
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const exchangeIds = data.exchangeIds || [];
      setExchangers(exchangeIds);
      
      // Cache the data
      exchangersCache.set('exchangers', exchangeIds);
      
      // Set default exchange if available
      if (exchangeIds.length > 0) {
        setSelectedExchange(exchangeIds[0]);
      }
    } catch (error) {
      console.error('Error fetching exchangers:', error);
      setError('Failed to load exchangers. Please try again later.');
    } finally {
      setIsLoadingExchangers(false);
    }
  }, [selectedExchange]);

  useEffect(() => {
    if (activeTab === 'trade-compass' && exchangers.length === 0) {
      fetchExchangers();
    }
  }, [activeTab, exchangers.length, fetchExchangers]);

  // Fetch instruments when exchange is selected
  useEffect(() => {
    if (selectedExchange) {
      fetchInstruments(selectedExchange);
    }
  }, [selectedExchange]);

  const fetchInstruments = useCallback(async (exchangeId: string) => {
    // Always reset selected instrument when exchange changes
    setSelectedInstrument('');
    
    // Check cache first
    if (instrumentsCache.has(exchangeId)) {
      const cachedInstruments = instrumentsCache.get(exchangeId) || [];
      setInstruments(cachedInstruments);
      
      // Set default instrument if available
      if (cachedInstruments.length > 0) {
        setSelectedInstrument(cachedInstruments[0].symbol);
      }
      return;
    }
    
    setIsLoadingInstruments(true);
    setError('');
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/chart/${exchangeId}/instruments`);
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const instruments = data.payload || [];
      setInstruments(instruments);
      
      // Cache the data
      instrumentsCache.set(exchangeId, instruments);
      
      // Set default instrument if available
      if (instruments.length > 0) {
        setSelectedInstrument(instruments[0].symbol);
      }
    } catch (error) {
      console.error('Error fetching instruments:', error);
      setError('Failed to load instruments. Please try again later.');
    } finally {
      setIsLoadingInstruments(false);
    }
  }, []);

  // Function to clear market brief input and results
  const clearMarketBrief = () => {
    setMarketBriefQuery('');
    setMarketBriefResult(null);
    setMarketBriefError('');
  };

  // Function to generate market brief
  const generateMarketBrief = async () => {
    if (!marketBriefQuery.trim()) {
      setMarketBriefError('Please enter a question, URL, or text to analyze');
      return;
    }
    
    setIsGeneratingMarketBrief(true);
    setMarketBriefError('');
    setMarketBriefResult(null);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/market/brief`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: marketBriefQuery
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setMarketBriefResult(data);
    } catch (error) {
      console.error('Error generating market brief:', error);
      setMarketBriefError('Failed to generate market brief. Please try again later.');
    } finally {
      setIsGeneratingMarketBrief(false);
    }
  };

  // Function to render the appropriate tool content based on the active tab
  const renderToolContent = () => {
    switch (activeTab) {
      case 'market-brief':
        return (
          <div>
            <h2 className="text-xl font-bold mb-4 dark:text-white">Market Brief</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Analyze financial news, articles, or ask questions about market trends.
            </p>
            
            <div className="mb-6">
              <label htmlFor="marketBriefQuery" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enter your question, URL, or paste text to analyze:
              </label>
              <textarea
                id="marketBriefQuery"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                rows={5}
                value={marketBriefQuery}
                onChange={(e) => setMarketBriefQuery(e.target.value)}
                placeholder="E.g., What's the current market sentiment for tech stocks? Or paste a news article or URL here..."
              />
            </div>
            
            <div className="flex justify-end space-x-3 mb-6">
              <button
                className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 disabled:opacity-50"
                onClick={clearMarketBrief}
                disabled={isGeneratingMarketBrief || (!marketBriefQuery.trim() && !marketBriefResult)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear
              </button>
              <button
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg flex items-center justify-center hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
                onClick={generateMarketBrief}
                disabled={isGeneratingMarketBrief || !marketBriefQuery.trim()}
              >
                {isGeneratingMarketBrief ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Report
                  </>
                )}
              </button>
            </div>
            
            {marketBriefError && (
              <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-4 mb-6">
                <p className="text-red-700 dark:text-red-400">{marketBriefError}</p>
              </div>
            )}
            
            {marketBriefResult && (
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold dark:text-white">Analysis Result</h3>
                  <div className="flex items-center">
                    <span className="text-sm mr-2 dark:text-gray-300">Sentiment:</span>
                    <span 
                      className={`px-3 py-1 rounded-full text-xs font-medium ${marketBriefResult.label === 'Bullish' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                        : marketBriefResult.label === 'Bearish' 
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' 
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}`}
                    >
                      {marketBriefResult.label} ({(marketBriefResult.score * 100).toFixed(1)}%)
                    </span>
                  </div>
                </div>
                
                <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                  <ReactMarkdown>
                    {marketBriefResult.marketBrief}
                  </ReactMarkdown>
                </div>
              </div>
            )}
            
            {!marketBriefResult && !isGeneratingMarketBrief && !marketBriefError && (
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg mb-4">
                <p className="dark:text-gray-300">With this tool you can:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1 dark:text-gray-300">
                  <li>Paste financial news articles or press releases</li>
                  <li>Ask questions about market trends and conditions</li>
                  <li>Get sentiment analysis specific to financial markets</li>
                  <li>Identify bullish, bearish, or neutral indicators</li>
                </ul>
              </div>
            )}
          </div>
        );
      case 'trade-compass':
        return (
          <div>
            <h2 className="text-xl font-bold mb-4 dark:text-white">Trade Compass</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              AI-generated summary of a stock's daily price action and news.
            </p>
            
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            
            {/* Debug button - only visible in development */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mb-4 text-right">
                <button 
                  onClick={() => {
                    exchangersCache.clear();
                    instrumentsCache.clear();
                  }}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline"
                >
                  Clear Cache
                </button>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Exchangers Dropdown */}
              <div>
                <label htmlFor="exchanger-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Exchange
                </label>
                <div className="relative">
                  <select
                    id="exchanger-select"
                    className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-blue-300 dark:hover:border-blue-700 transition-colors cursor-pointer"
                    value={selectedExchange}
                    onChange={(e) => setSelectedExchange(e.target.value)}
                    disabled={isLoadingExchangers}
                  >
                    <option value="">Select an exchange</option>
                    {exchangers.map((exchange) => (
                      <option key={exchange} value={exchange} className="py-2">
                        {exchange}
                      </option>
                    ))}
                  </select>
                  {isLoadingExchangers && (
                    <div className="absolute right-2 top-2">
                      <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Instruments Dropdown */}
              <div>
                <label htmlFor="instrument-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Instrument
                </label>
                <div className="relative">
                  <select
                    id="instrument-select"
                    className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-green-300 dark:hover:border-green-700 transition-colors cursor-pointer"
                    value={selectedInstrument}
                    onChange={(e) => setSelectedInstrument(e.target.value)}
                    disabled={isLoadingInstruments || !selectedExchange}
                  >
                    <option value="">Select an instrument</option>
                    {instruments.map((instrument, index) => (
                      <option key={`${instrument.symbol}-${index}`} value={instrument.symbol} className="py-2">
                        {instrument.symbol} - {instrument.description}
                      </option>
                    ))}
                  </select>
                  {isLoadingInstruments && (
                    <div className="absolute right-2 top-2">
                      <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Timeframe Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Timeframe
              </label>
              <div className="flex flex-wrap gap-3">
                <label 
                  className={`flex items-center justify-center px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${timeframe === '4h' 
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-600 text-blue-700 dark:text-blue-300 shadow-sm' 
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    name="timeframe"
                    value="4h"
                    checked={timeframe === '4h'}
                    onChange={() => setTimeframe('4h')}
                  />
                  <div className="flex flex-col items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">4 Hours</span>
                  </div>
                </label>
                
                <label 
                  className={`flex items-center justify-center px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${timeframe === '1D' 
                    ? 'bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300 shadow-sm' 
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    name="timeframe"
                    value="1D"
                    checked={timeframe === '1D'}
                    onChange={() => setTimeframe('1D')}
                  />
                  <div className="flex flex-col items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium">1 Day</span>
                  </div>
                </label>
                
                <label 
                  className={`flex items-center justify-center px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${timeframe === '1W' 
                    ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-500 dark:border-purple-600 text-purple-700 dark:text-purple-300 shadow-sm' 
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    name="timeframe"
                    value="1W"
                    checked={timeframe === '1W'}
                    onChange={() => setTimeframe('1W')}
                  />
                  <div className="flex flex-col items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <span className="font-medium">1 Week</span>
                  </div>
                </label>
              </div>
            </div>
            
            <div className="mt-6">
              <button 
                className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 hover:from-blue-700 hover:to-blue-800 dark:hover:from-blue-600 dark:hover:to-blue-700 text-white font-medium py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg flex items-center justify-center"
                disabled={!selectedExchange || !selectedInstrument || isLoadingExchangers || isLoadingInstruments || isGeneratingBrief}
                onClick={generateTradeCompass}
              >
                {isGeneratingBrief ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Analyze
                  </>
                )}
              </button>
            </div>
            
            {chartData ? (
              <div className="mt-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="p-4 border-b dark:border-gray-700">
                  <h3 className="text-lg font-semibold dark:text-white">Market Analysis</h3>
                </div>
                
                {chartData && chartData.image && (
                  <div className="p-4 border-b dark:border-gray-700">
                    <h4 className="text-md font-medium mb-2 dark:text-gray-200">Chart</h4>
                    <div className="mt-2">
                      <img 
                        src={`data:image/png;base64,${chartData.image}`} 
                        alt="Market Chart" 
                        className="w-full rounded-lg shadow-md cursor-pointer transform transition-all hover:shadow-xl hover:scale-[1.01] border-2 border-transparent hover:border-blue-300 dark:hover:border-blue-700"
                        onClick={() => setShowChartModal(true)}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">Click chart to enlarge</p>
                    </div>
                  </div>
                )}
                
                {chartData.analysis && (
                  <div className="p-4">
                    <h4 className="text-lg font-semibold mb-4 dark:text-gray-200 border-b dark:border-gray-700 pb-2">Trading Recommendation</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-transparent hover:border-gray-200 dark:hover:border-gray-700">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Direction</p>
                        <p className={`text-xl font-bold ${chartData.analysis.direction === 'BUY' ? 'text-green-600 dark:text-green-400' : chartData.analysis.direction === 'SELL' ? 'text-red-600 dark:text-red-400' : 'dark:text-white'}`}>
                          {chartData.analysis.direction === 'BUY' && (
                            <span className="inline-flex items-center">
                              <span>BUY</span>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                              </svg>
                            </span>
                          )}
                          {chartData.analysis.direction === 'SELL' && (
                            <span className="inline-flex items-center">
                              <span>SELL</span>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                              </svg>
                            </span>
                          )}
                          {!chartData.analysis.direction && 'N/A'}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-transparent hover:border-gray-200 dark:hover:border-gray-700">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Entry Price</p>
                        <p className="text-xl font-bold dark:text-white">{chartData.analysis.entryPrice || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-transparent hover:border-gray-200 dark:hover:border-gray-700">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Stop Loss</p>
                        <p className="text-xl font-bold text-red-600 dark:text-red-400">{chartData.analysis.stopLoss || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-transparent hover:border-gray-200 dark:hover:border-gray-700">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Take Profit</p>
                        <p className="text-xl font-bold text-green-600 dark:text-green-400">{chartData.analysis.takeProfit1 || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-transparent hover:border-gray-200 dark:hover:border-gray-700">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Risk/Reward</p>
                        <p className="text-xl font-bold dark:text-white">{chartData.analysis.riskRewardRatio || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Confidence</p>
                        {chartData.analysis.confidenceScore ? (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-lg font-semibold dark:text-white">
                                {Math.round((chartData.analysis.confidenceScore / 10) * 100)}%
                              </p>
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                {getConfidenceLabel(chartData.analysis.confidenceScore)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                              <div 
                                className={`h-2.5 rounded-full ${getConfidenceColor(chartData.analysis.confidenceScore)}`}
                                style={{ width: `${(chartData.analysis.confidenceScore / 10) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-lg font-semibold dark:text-white">N/A</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg mt-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Select options above and click Generate</p>
                <p className="dark:text-gray-300">This tool will allow you to:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1 dark:text-gray-300">
                  <li>Get a concise summary of the selected instrument's price movement</li>
                  <li>View relevant news that affected the instrument</li>
                  <li>Receive key technical indicators and support/resistance levels</li>
                </ul>
              </div>
            )}
          </div>
        );
      default:
        return <div className="dark:text-gray-300">Select a tool to get started</div>;
    }
  };
  
  // Function to clear cache for testing purposes
  const clearCache = () => {
    exchangersCache.clear();
    instrumentsCache.clear();
    setExchangers([]);
    setInstruments([]);
    setSelectedExchange('');
    setSelectedInstrument('');
    setChartData(null);
    fetchExchangers();
  };
  
  // Function to generate trade compass data
  const generateTradeCompass = async () => {
    if (!selectedExchange || !selectedInstrument) return;
    
    setIsGeneratingBrief(true);
    setError('');
    setChartData(null);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/chart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exchange: selectedExchange,
          instrument: selectedInstrument,
          timeframe: timeframe
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setChartData(data);
    } catch (error) {
      console.error('Error generating trade compass data:', error);
      setError('Failed to generate trade compass data. Please try again later.');
    } finally {
      setIsGeneratingBrief(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 shadow-md">
        <div className="container mx-auto px-4">
          <button 
            className="mb-3 px-3 py-1 bg-white/20 rounded hover:bg-white/40 text-sm transition-all duration-300 active:scale-90 active:translate-x-[-4px]"
            onClick={() => navigate('/')}
          >
            ← Back
          </button>
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h1 className="text-3xl font-bold">Financial and Market Tools</h1>
            </div>
            <p className="text-blue-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
              Tools for analyzing financial news, market data, and investment insights
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Tool Selection Tabs */}
          <div className="flex mb-6 border-b dark:border-gray-700">
            <button
              className={`px-6 py-3 font-medium rounded-t-lg flex flex-col items-center ${activeTab === 'market-brief' 
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30'}`}
              onClick={() => setActiveTab('market-brief')}
            >
              <span className="text-lg font-bold">Market Brief</span>
              <span className={`text-xs mt-1 ${activeTab === 'market-brief' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                Analyze financial news for market sentiment
              </span>
            </button>
            <button
              className={`px-6 py-3 font-medium rounded-t-lg flex flex-col items-center ${activeTab === 'trade-compass' 
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/30'}`}
              onClick={() => setActiveTab('trade-compass')}
            >
              <span className="text-lg font-bold">Trade Compass</span>
              <span className={`text-xs mt-1 ${activeTab === 'trade-compass' ? 'text-green-100' : 'text-gray-500 dark:text-gray-400'}`}>
                AI-generated daily stock market summaries
              </span>
            </button>
          </div>
          
          {/* Tool Content */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            {renderToolContent()}
          </div>
        </div>
      </div>
    
      {/* Chart Modal */}
      {showChartModal && chartData && chartData.image && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50" onClick={() => setShowChartModal(false)}>
          <div className="relative w-full h-full flex items-center justify-center p-2 md:p-4">
            <div className="absolute top-4 right-4 z-10">
              <button 
                className="bg-gray-800 hover:bg-gray-700 text-white rounded-full p-2 focus:outline-none shadow-lg"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowChartModal(false);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <img 
              src={`data:image/png;base64,${chartData.image}`} 
              alt="Market Chart Enlarged" 
              className="max-h-[95vh] max-w-[95vw] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialToolsPage;
